/**
 * The "Notify me" bell on each skill in the profile.
 *
 * `notify_on_match` decides who gets the closing-soon reminder
 * (services/closing_soon_reminder.py), and that mail says to turn it off here.
 * Until this existed the flag could only be changed through the API, so the
 * mail pointed at a control nobody could find.
 */

import { Tooltip, makeStyles, mergeClasses, tokens } from '@fluentui/react-components'
import { Alert16Regular, AlertOff16Regular } from '@fluentui/react-icons'
import { useToast } from './ToastProvider'
import { useUpdateSkill } from '../hooks/useUserSkills'
import { apiErrorMessage } from '../lib/apiError'
import { bayerColors } from '../theme'
import type { UserSkill } from '../types/capability'

const useStyles = makeStyles({
  // The same bare button as the chip's remove control beside it.
  bell: {
    cursor: 'pointer',
    display: 'inline-flex',
    background: 'transparent',
    border: 'none',
    padding: 0,
    color: bayerColors.navy,
    ':hover': { color: bayerColors.navyMid },
  },
  off: { color: tokens.colorNeutralForeground3 },
})

export function SkillNotifyToggle({ skill }: { skill: UserSkill }) {
  const styles = useStyles()
  const { showToast } = useToast()
  const update = useUpdateSkill()
  // While a save is in flight, show the state being saved. The hook stays
  // pending until the refetched list lands, so there is no flicker back; a
  // failure falls through to what the server still holds.
  const on =
    (update.isPending ? update.variables?.body.notify_on_match : undefined) ??
    skill.notify_on_match

  const toggle = () => {
    if (update.isPending) return
    update.mutate(
      { id: skill.id, body: { notify_on_match: !on } },
      {
        onError: (err) =>
          showToast({
            title: `Could not update Notify me for ${skill.capability_name}`,
            body: apiErrorMessage(err),
            intent: 'error',
          }),
      },
    )
  }

  return (
    <Tooltip
      content="Email me when a demand needing this skill is about to close"
      relationship="description"
    >
      <button
        type="button"
        className={mergeClasses(styles.bell, !on && styles.off)}
        aria-label={`Notify me about ${skill.capability_name}`}
        aria-pressed={on}
        onClick={toggle}
      >
        {on ? <Alert16Regular /> : <AlertOff16Regular />}
      </button>
    </Tooltip>
  )
}
